import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { sendPlaylistEvent } from '../../utils/playlistEvents';

export default function QuizBlock({ block, playlist, blockIndex, onComplete, persistentCTA }) {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [watchStartTime] = useState(Date.now());

  const question = block.props?.question || '';
  const options = block.props?.options || [];
  const correctIndex = block.props?.correct_index;
  const hasCorrectAnswer = correctIndex !== undefined && correctIndex !== null && correctIndex >= 0;
  const feedback = block.props?.feedback || { correct: 'Correct!', incorrect: 'Try again!' };

  useEffect(() => {
    sendPlaylistEvent(playlist.id, 'block_start', {
      block_id: block.id,
      block_index: blockIndex,
      block_type: 'quiz'
    });
    
    sendPlaylistEvent(playlist.id, 'quiz_shown', {
      block_id: block.id,
      block_index: blockIndex,
      question: question
    });
  }, [block.id, blockIndex, playlist.id, question]);

  const handleAnswerSelect = (index) => {
    if (showFeedback) return; // Already answered

    setSelectedAnswer(index);
    
    let correct = null;
    if (hasCorrectAnswer) {
      correct = index === correctIndex;
      setIsCorrect(correct);
    }
    // If no correct answer, correct remains null
    
    setShowFeedback(true);

    sendPlaylistEvent(playlist.id, 'quiz_answer', {
      block_id: block.id,
      block_index: blockIndex,
      answer_index: index,
      correct: correct,
      has_correct_answer: hasCorrectAnswer
    });

    if (correct) {
      sendPlaylistEvent(playlist.id, 'quiz_correct', {
        block_id: block.id,
        block_index: blockIndex
      });
    }
  };

  const handleContinue = () => {
    sendPlaylistEvent(playlist.id, 'block_complete', {
      block_id: block.id,
      block_index: blockIndex,
      watch_ms: Date.now() - watchStartTime
    });
    if (onComplete) onComplete();
  };

  const handleCTAClick = useCallback((e) => {
    e.stopPropagation();
    sendPlaylistEvent(playlist.id, 'cta_click', {
      block_id: block.id,
      block_index: blockIndex,
      url: persistentCTA?.url,
      label: persistentCTA?.label
    });
    if (persistentCTA?.url) {
      window.open(persistentCTA.url, '_blank', 'noopener,noreferrer');
    }
  }, [playlist.id, block.id, blockIndex, persistentCTA]);

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-green-900 to-teal-900 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Question */}
        <h3 className="text-white text-2xl md:text-3xl font-bold mb-8 text-center">
          {question}
        </h3>

        {/* Options */}
        <div className="space-y-4 mb-8">
          {options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrectOption = index === correctIndex;
            
            let bgColor = 'bg-white/10 hover:bg-white/20';
            let borderColor = 'border-white/30';
            let showIcon = null;
            
            if (showFeedback) {
              if (hasCorrectAnswer) {
                // Normal quiz: show correct/incorrect
                if (isSelected && isCorrect) {
                  bgColor = 'bg-green-500';
                  borderColor = 'border-green-400';
                  showIcon = 'correct';
                } else if (isSelected && !isCorrect) {
                  bgColor = 'bg-red-500';
                  borderColor = 'border-red-400';
                  showIcon = 'incorrect';
                } else if (isCorrectOption) {
                  bgColor = 'bg-green-500/50';
                  borderColor = 'border-green-400';
                }
              } else {
                // No correct answer: neutral styling
                if (isSelected) {
                  bgColor = 'bg-purple-500/50';
                  borderColor = 'border-purple-400';
                  showIcon = 'neutral';
                }
              }
            }

            return (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={showFeedback}
                className={`w-full p-4 rounded-lg border-2 transition-all text-white text-left flex items-center justify-between ${
                  bgColor
                } ${borderColor} ${showFeedback ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className="text-lg">{option}</span>
                {showFeedback && showIcon === 'correct' && (
                  <CheckCircle2 className="h-6 w-6 text-white" />
                )}
                {showFeedback && showIcon === 'incorrect' && (
                  <XCircle className="h-6 w-6 text-white" />
                )}
                {showFeedback && showIcon === 'neutral' && (
                  <span className="text-sm font-medium">Your answer</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Feedback */}
        {showFeedback && (
          <div className="text-center mb-6">
            {hasCorrectAnswer ? (
              <p className="text-white text-xl mb-4">
                {isCorrect ? feedback.correct : feedback.incorrect}
              </p>
            ) : (
              <p className="text-purple-200 text-lg mb-4">
                💭 This question has no right or wrong answer
              </p>
            )}
            <Button
              onClick={handleContinue}
              size="lg"
              className="bg-white text-black hover:bg-gray-200"
            >
              Verder
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Persistent CTA (show after answering) */}
        {persistentCTA && showFeedback && (
          <div className="text-center mt-4">
            <Button
              onClick={handleCTAClick}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {persistentCTA.label}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
